import React, { useEffect, useState, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { TabView, TabPanel } from 'primereact/tabview';
import { useParams, useLocation } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { Badge } from 'primereact/badge';
import { Calendar } from 'primereact/calendar';
import { Password } from 'primereact/password';
import { Check, Copy } from 'lucide-react';
import FileUploader from '../../components/FileUploader/FileUploader';
import AppBreadcrumb from '../../components/CustomeBreadcrumb/AppBreadcrumb';
import AddNote from '../../components/AddNote/addNote';
import CopyAllButton from '../../components/CopyAllButton/CopyAllButton';
import {
    COMPANY_TAX_FILING_LIST,
    COMPANY_GST_FILING_LIST,
    COMPANY_WCB_FILING_LIST,
    COMPANY_TAX_FILING_STATUS_CHANGE,
    COMPANY_GST_FILING_STATUS_CHANGE,
    COMPANY_WCB_FILING_STATUS_CHANGE,
    UPLOAD_COMPANY_SIGNED_TAX_FILING_DOC,
    UPLOAD_COMPANY_TAX_FILING_DOC,
    COMPANY_TAX_FILING_PAYMENT,
    LIST_COMPANIES,
    CHANGE_TAX_FILING_STATUS_OF_COMPANY
} from '../../config/ApiConstant';
import { getAllDetails, updateData } from '../../service/ApiService';
import { TaxFilingColumns, GstFilingColumns, WcbFilingColumns, ColumnMeta } from '../../appconfig/DatatableSetting';
import { SkeletonbodyTemplate, Skeletonitems, defaultPageRowOptions, defaultRowOptions, paginatorLinks, showingEntries, APP_BASE_URL, PendingFiledStatusOptions, CompanyFilingStatusOptions, CompanyDocumentStatusOptions } from '../../appconfig/Settings';
import { InputNumber } from 'primereact/inputnumber';
import { formatdate, globalDateTemplate } from '../../appconfig/AppHelper';
import { validateCompanyT183Upload } from '../../config/Validate';

const FilingTable = ({ api, statusApi, companyId, columns, paymentApi, type }: { api: string, statusApi: string, companyId: string, columns: ColumnMeta[], paymentApi?: string, type: 'tax' | 'gst' | 'wcb' }) => {
    const toast = useRef<any>(null);
    // List of filing records state
    const [list, setList] = useState<any[]>([]);
    // Data loading status state
    const [loading, setLoading] = useState<boolean>(true);
    // Status modal visibility state
    const [statusModal, setStatusModal] = useState<boolean>(false);
    // Upload modal visibility state
    const [uploadModal, setUploadModal] = useState<boolean>(false);
    // Currently selected filing record state
    const [selectedFiling, setSelectedFiling] = useState<any>(null);
    // Selected database field name state
    const [selectedField, setSelectedField] = useState<string>('');
    // New status value to update
    const [newStatus, setNewStatus] = useState<string>('');
    // Password required to change a status away from Done
    const [statusPassword, setStatusPassword] = useState<string>('');
    // Validation error for the status password field
    const [statusPasswordError, setStatusPasswordError] = useState<string>('');
    // Selected file for upload state
    const [selectedFile, setSelectedFile] = useState<any>(null);
    // URL of uploaded document state
    const [uploadedUrl, setUploadedUrl] = useState<string>('');
    // Active API endpoint for upload
    const [activeUploadApi, setActiveUploadApi] = useState<string>('');
    // Success feedback modal visibility state
    const [successModal, setSuccessModal] = useState<boolean>(false);
    // Data for success feedback state
    const [successData, setSuccessData] = useState<any>({});
    // Form submission loading status state
    const [submitLoading, setSubmitLoading] = useState<boolean>(false);
    // Balance input enablement status state
    const [isBalanceEnabled, setIsBalanceEnabled] = useState<boolean>(false);
    // Outstanding balance amount state
    const [balanceAmount, setBalanceAmount] = useState<number | null>(null);
    // Payment modal visibility state
    const [paymentModal, setPaymentModal] = useState<boolean>(false);
    // Payment amount value state
    const [paymentValue, setPaymentValue] = useState<number | null>(null);
    // Selected year for filtering state
    const [selectedYear, setSelectedYear] = useState<any>(() => {
        const savedYear = localStorage.getItem('selectedFilingYear');
        return savedYear ? parseInt(savedYear, 10) : new Date().getFullYear();
    });
    // Note modal visibility state
    const [addNote, setAddNote] = useState<boolean>(false);
    // Content of note text state
    const [noteText, setNoteText] = useState<string>('');
    // Note saving loading status state
    const [noteLoading, setNoteLoading] = useState<boolean>(false);
    // List of read-only files state
    const [readFiles, setReadFiles] = useState<any[]>([]);
    // Profit and loss file state
    const [profitAndLossFile, setProfitAndLossFile] = useState<any>(null);
    // Balance sheet file state
    const [balanceSheetFile, setBalanceSheetFile] = useState<any>(null);
    // T183 link copy status flag
    const [whatsappT183Copy, setWhatsappT183Copy] = useState<boolean>(false);
    // P&L link copy status flag
    const [whatsappProfitAndLossCopy, setWhatsappProfitAndLossCopy] = useState<boolean>(false);
    // Balance sheet copy status flag
    const [whatsappBalanceSheetCopy, setWhatsappBalanceSheetCopy] = useState<boolean>(false);
    // Validation errors for uploads
    const [uploadErrors, setUploadErrors] = useState<any>({});


    const dataTableRef = useRef<any>(null);
    const topScrollRef = useRef<any>(null);


    const statusColors: Record<string, string> = {
        'Done': '#16a34a',
        'N/A': '#9ca3af',
        'Signed': '#8b5cf6',
        'Pending': '#f59e0b',
        'Received': '#16a34a',
        'Filed': '#16a34a',
        'Completed': '#16a34a'
    };

    // Fetch list on mount/update
    useEffect(() => {
        if (companyId) getList();
    }, [api, companyId, selectedYear]);

    // Save selected year to storage
    useEffect(() => {
        if (selectedYear) {
            localStorage.setItem('selectedFilingYear', selectedYear.toString());
        }
    }, [selectedYear]);

    // Template for status dropdown items
    const statusItemTemplate = (option: any) => (
        <span style={{ color: statusColors[option.value] || 'inherit', fontWeight: "bold" }}>{option.label}</span>
    );

    // Template for current status value
    const statusValueTemplate = (option: any, props: any) => {
        if (!option) return <span>{props.placeholder}</span>;
        return (
            <span style={{ color: statusColors[option.value] || 'inherit', fontWeight: "bold" }}>
                {option.label}
            </span>
        );
    };

    // Fetch filing list from API
    const getList = async () => {
        setLoading(true);
        try {
            const url = selectedYear ? `${api}/${companyId}?year=${selectedYear}` : `${api}/${companyId}`;
            const response = await getAllDetails(url);
            setList(response?.data || response || []);
            setReadFiles(response?.read_files || []);
        } catch (error) {
            setList([]);
            setReadFiles([]);
        } finally {
            setLoading(false);
        }
    };

    // Open payment link modal
    const handlePaymentClick = (rowData: any) => {
        const payment = rowData.temp_payments && rowData.temp_payments.length > 0 ? rowData.temp_payments[0] : null;
        setSelectedFiling(rowData);
        setPaymentValue(payment ? parseFloat(payment.amount) : 0);
        setNewStatus(payment ? payment.status : 'Completed');
        setPaymentModal(true);
    };

    // Open document upload modal
    const handleUploadClick = (rowData: any, field: string, apiEndpoint?: string) => {
        setSelectedFiling(rowData);
        setSelectedField(field);
        setUploadedUrl(rowData[`${field}_url`] || '');
        setActiveUploadApi(apiEndpoint || statusApi);
        setUploadModal(true);
    };

    // Copy text to clipboard
    const handleCopy = async (text: string, type: "t183" | "profit_loss" | "balance_sheet" | null = null) => {
        if (!text) return;
        await navigator.clipboard.writeText(text);

        if (type === "t183") setWhatsappT183Copy(true);
        else if (type === "profit_loss") setWhatsappProfitAndLossCopy(true);
        else if (type === "balance_sheet") setWhatsappBalanceSheetCopy(true);

        setTimeout(() => {
            if (type === "t183") setWhatsappT183Copy(false);
            else if (type === "profit_loss") setWhatsappProfitAndLossCopy(false);
            else if (type === "balance_sheet") setWhatsappBalanceSheetCopy(false);
        }, 5000);
    };

    // Copy combined document URLs
    // Copy combined document URLs feedback
    const handleCopyAllFinish = () => {
        setWhatsappT183Copy(true);
        if (successData.readDocUrls && successData.readDocUrls.length > 0) {
            setWhatsappProfitAndLossCopy(true);
            if (successData.readDocUrls.length > 1) {
                setWhatsappBalanceSheetCopy(true);
            }
        }

        setTimeout(() => {
            setWhatsappT183Copy(false);
            setWhatsappProfitAndLossCopy(false);
            setWhatsappBalanceSheetCopy(false);
        }, 5000);
    };


    // Open signed PDF in browser
    const handleViewPdf = (rowData: any) => {
        try {
            // get first document (or find signed one)
            const doc = rowData?.documents?.find((d: any) => d.signed_doc_url)
                || rowData?.documents?.[0];

            const urlToOpen = doc?.signed_doc_url;

            if (urlToOpen) {
                window.open(urlToOpen, '_blank');
            } else {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Info',
                    detail: 'Signed document is not available.'
                });
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to open document'
            });
        }
    };

    // Save status change via API
    const saveStatus = async () => {
        const passwordRequired = selectedFiling?.[selectedField] === 'Done';
        if (passwordRequired && !statusPassword.trim()) {
            setStatusPasswordError('Password is required to change status from Done');
            return;
        }
        setStatusPasswordError('');
        try {
            setSubmitLoading(true);
            const apiToUpdate = selectedField === 'document_status' ? CHANGE_TAX_FILING_STATUS_OF_COMPANY : statusApi;
            const payload: any = { [selectedField]: newStatus };
            if (passwordRequired) payload.password = btoa(unescape(encodeURIComponent(statusPassword)));
            const response = await updateData(apiToUpdate, selectedFiling.id, payload);
            if (response) {
                toast.current?.show({ severity: 'success', summary: 'Success', detail: response.message || 'Status updated successfully' });
                setStatusModal(false);
                setStatusPassword('');
                // Optimistic update to UI
                setList(prevList => prevList.map(item => String(item.id) === String(selectedFiling.id) ? { ...item, [selectedField]: newStatus } : item));
                getList();
            }
        } catch (error: any) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: error?.response?.data?.message || 'Failed to update status' });
        } finally {
            setSubmitLoading(false);
        }
    };

    // Handle document upload via API
    const saveUpload = async () => {
        // Specific validation for t183_doc which requires all three documents
        if (selectedField === 't183_doc' && activeUploadApi === UPLOAD_COMPANY_TAX_FILING_DOC) {
            const { isError, errors } = validateCompanyT183Upload(selectedFile, profitAndLossFile, balanceSheetFile);
            setUploadErrors(errors);
            if (isError) return;
        } else {
            // General validation: at least one file must be selected for other cases
            if (!selectedFile && !profitAndLossFile && !balanceSheetFile) return;
        }

        setUploadErrors({});
        try {
            setSubmitLoading(true);
            const formData = new FormData();
            if (selectedFile) formData.append('file', selectedFile);

            if (profitAndLossFile) formData.append('read_files', profitAndLossFile);
            if (balanceSheetFile) formData.append('read_files', balanceSheetFile);

            // Handle existing readFiles dynamic field button cases if any
            if (activeUploadApi === UPLOAD_COMPANY_TAX_FILING_DOC && selectedField.startsWith('read_doc_') && selectedFile) {
                formData.append('read_files', selectedFile);
            }

            if (isBalanceEnabled && balanceAmount !== null) {
                formData.append('balance_amount', balanceAmount.toString());
            }

            // Per instructions: signed upload uses document ID, regular upload uses row ID
            const uploadId = activeUploadApi === UPLOAD_COMPANY_SIGNED_TAX_FILING_DOC
                ? (selectedFiling.t183_doc_id || selectedFiling.id)
                : selectedFiling.id;

            const response = await updateData(activeUploadApi, uploadId, formData);
            if (response) {
                toast.current?.show({ severity: 'success', summary: 'Success', detail: response.message || 'File uploaded successfully' });
                setUploadModal(false);
                setSelectedFile(null);
                setProfitAndLossFile(null);
                setBalanceSheetFile(null);

                // Handle multi-URL response
                if (response?.data && typeof response.data === 'object') {
                    setSuccessData(response.data);
                } else {
                    const singleUrl = typeof response?.data === 'string' ? response.data : (response?.data?.url || response?.url || '');
                    setSuccessData({ docUrl: singleUrl });
                }

                setSuccessModal(true);
                await getList();
            }
        } catch (error: any) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: error?.response?.data?.message || 'Failed to upload file' });
        } finally {
            setSubmitLoading(false);
        }
    };

    // Update payment amount via API
    const savePaymentAmount = async () => {
        if (paymentValue === null) return;
        try {
            setSubmitLoading(true);
            const payload: any = {
                amount: paymentValue,
                payment_status: newStatus,
                type: 'amount_update'
            };

            const ep = paymentApi || statusApi;
            const response = await updateData(ep, selectedFiling.id, payload);
            if (response) {
                toast.current?.show({ severity: 'success', summary: 'Success', detail: response.message || 'Payment updated successfully' });
                setPaymentModal(false);

                // Refresh list to show updated data
                await getList();
            }
        } catch (error: any) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: error?.response?.data?.message || 'Failed to update payment' });
        } finally {
            setSubmitLoading(false);
        }
    };

    // Prepare note for editing
    const handleNoteClick = (rowData: any, field: string) => {
        setSelectedFiling(rowData);
        setSelectedField(field);

        // Logic for mapping field to database note field
        let noteKey = field;
        if (field === 't5_return') noteKey = 't5_note';
        else if (field === 't4a_return') noteKey = 't4a_note';
        else if (field !== 'note' && !field.endsWith('_note')) {
            noteKey = `${field}_note`;
        }

        setNoteText(rowData[noteKey] || '');
        setAddNote(true);
    };

    // Save note content via API
    const saveNoteHandler = async () => {
        try {
            setNoteLoading(true);
            let noteKey = selectedField;
            if (selectedField === 't5_return') noteKey = 't5_note';
            else if (selectedField === 't4a_return') noteKey = 't4a_note';
            else if (selectedField !== 'note' && !selectedField.endsWith('_note')) {
                noteKey = `${selectedField}_note`;
            }

            const response = await updateData(statusApi, selectedFiling.id, {
                [noteKey]: noteText
            });
            if (response) {
                toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Notes added successfully' });
                setAddNote(false);
                getList();
            }
        } catch (error: any) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: error?.response?.data?.message || 'Failed to save note' });
        } finally {
            setNoteLoading(false);
        }
    };

    // --- Body Templates ---

    // Template for payment column
    const paymentBodyTemplate = (rowData: any) => {
        const payment = rowData.temp_payments && rowData.temp_payments.length > 0 ? rowData.temp_payments[0] : null;

        if (!payment) {
            return (
                <Button
                    icon="pi pi-plus"
                    className="common-primary-button"
                    tooltip="Add Payment"
                    tooltipOptions={{ position: 'bottom' }}
                    onClick={() => handlePaymentClick(rowData)}
                />
            );
        }

        return (
            <div className="flex align-items-center gap-1">
                <div className="flex flex-column align-items-center gap-2">
                    <span className="font-bold">${payment.amount}</span>
                    <Badge
                        severity={payment.status === 'Completed' ? 'success' : 'warning'}
                        value={payment.status}
                        className="font-bold"
                    />
                </div>
                {payment.status !== 'Completed' && (
                    <Button
                        icon="pi pi-dollar"
                        className="common-primary-button ml-2"
                        style={{ width: '1.8rem', height: '1.8rem' }}
                        tooltip="Resend Payment Link"
                        tooltipOptions={{ position: 'bottom' }}
                        onClick={() => handlePaymentClick(rowData)}
                    />
                )}
            </div>
        );
    };

    // Template for clickable status dropdown
    const renderClickableStatus = (rowData: any, field: string) => {
        const val = rowData[field] || 'Pending';
        const isFiledOrDone = val === 'Filed';

        return (
            <Dropdown
                value={val}
                options={type === 'tax' ? CompanyFilingStatusOptions : PendingFiledStatusOptions}
                placeholder="Select Status"
                className="w-full"
                onChange={(e) => {
                    setSelectedFiling(rowData);
                    setSelectedField(field);
                    setNewStatus(e.value);
                    setStatusPassword('');
                    setStatusPasswordError('');
                    setStatusModal(true);
                }}
                itemTemplate={statusItemTemplate}
                valueTemplate={statusValueTemplate}
                disabled={isFiledOrDone}
            />
        );
    };

    // Render link to view document
    const renderFileLink = (rowData: any, field: string) => {
        const fileUrl = rowData[`${field}_url`];
        if (!fileUrl) return null;
        return (
            <Button
                icon="pi pi-file-pdf"
                className="p-button-secondary ml-2 common-view-pdf-btn"
                tooltip="View PDF"
                tooltipOptions={{ position: 'bottom' }}
                onClick={() => window.open(fileUrl, '_blank')}
            />
        );
    };

    // Wrap content with note button
    const wrapWithNotes = (rowData: any, field: string, content: React.ReactNode) => {
        let noteKey = field;
        if (field === 't5_return') noteKey = 't5_note';
        else if (field === 't4a_return') noteKey = 't4a_note';
        else if (field !== 'note' && !field.endsWith('_note')) {
            noteKey = `${field}_note`;
        }

        const note = rowData[noteKey];
        return (
            <div className="flex align-items-center gap-2">
                {content}
                <Button
                    icon="pi pi-comment"
                    style={{
                        backgroundColor: note ? 'red' : '#2563eb',
                        borderColor: note ? 'red' : '#2563eb',
                        color: 'white',
                        width: '2rem',
                        height: '2rem'
                    }}
                    onClick={() => handleNoteClick(rowData, field)}
                    tooltip={note || 'Add Notes'}
                    tooltipOptions={{ position: "bottom" }}
                />
            </div>
        );
    };

    // Template for general notes column
    const notesColumnTemplate = (rowData: any) => {
        const note = rowData.note;
        return (
            <div className="flex justify-content-center">
                <Button
                    icon="pi pi-comment"
                    style={{
                        backgroundColor: note ? 'red' : '#2563eb',
                        borderColor: note ? 'red' : '#2563eb',
                        color: 'white',
                        width: '2rem',
                        height: '2rem'
                    }}
                    onClick={() => handleNoteClick(rowData, 'note')}
                    tooltip={note || 'Add Notes'}
                    tooltipOptions={{ position: "bottom" }}
                />
            </div>
        );
    };

    const documentColumnTemplate = (rowData: any) => {
        const val = rowData.document_status || 'Pending';
        return (
            <Dropdown
                value={val}
                options={CompanyDocumentStatusOptions}
                placeholder="Select Status"
                className="w-full"
                onChange={(e) => {
                    setSelectedFiling(rowData);
                    setSelectedField('document_status');
                    setNewStatus(e.value);
                    setStatusPassword('');
                    setStatusPasswordError('');
                    setStatusModal(true);
                }}
                itemTemplate={statusItemTemplate}
                valueTemplate={statusValueTemplate}
            />
        );
    };

    const bankRecBodyTemplate = (rowData: any) => renderClickableStatus(rowData, 'bank_rec');
    // Credit card status column template
    const creditCardBodyTemplate = (rowData: any) => renderClickableStatus(rowData, 'credit_card');
    // Financials status column template
    const financialsBodyTemplate = (rowData: any) => renderClickableStatus(rowData, 'financials');

    // Combined status and document template
    const statusBodyTemplate = (rowData: any) => (
        <div className="flex align-items-center gap-2">
            {renderClickableStatus(rowData, 'status')}
            {renderFileLink(rowData, 'status')}
        </div>
    );

    const gstReturnBodyTemplate = (rowData: any) => (
        <div className="flex align-items-center gap-2">
            {renderClickableStatus(rowData, 'gst_return')}
            {renderFileLink(rowData, 'gst_return')}
        </div>
    );

    const wcbReturnBodyTemplate = (rowData: any) => (
        <div className="flex align-items-center gap-2">
            {renderClickableStatus(rowData, 'wcb_return')}
            {renderFileLink(rowData, 'wcb_return')}
        </div>
    );

    const t2ReturnBodyTemplate = (rowData: any) => (
        <div className="flex align-items-center gap-2">
            {renderClickableStatus(rowData, 't2_return')}
            {renderFileLink(rowData, 't2_return')}
        </div>
    );

    const t5ReturnBodyTemplate = (rowData: any) => wrapWithNotes(rowData, 't5_return', (
        <div className="flex align-items-center gap-2">
            {renderClickableStatus(rowData, 't5_return')}
            {renderFileLink(rowData, 't5_return')}
        </div>
    ));

    const t4aReturnBodyTemplate = (rowData: any) => wrapWithNotes(rowData, 't4a_return', (
        <div className="flex align-items-center gap-2">
            {renderClickableStatus(rowData, 't4a_return')}
            {renderFileLink(rowData, 't4a_return')}
        </div>
    ));

    // Template for T183 document column
    const t183DocBodyTemplate = (rowData: any) => {
        const val = rowData.t183_doc || 'Pending';
        const isFiled = val === 'Filed' || val === 'Signed';

        return (
            <div className="flex align-items-center gap-2">
                <span style={{ color: statusColors[val] || 'inherit', fontWeight: 'bold' }}>
                    {val}
                </span>
                <Button
                    icon={isFiled ? "pi pi-check-square" : "pi pi-file-edit"}
                    className="ml-2 common-primary-button"
                    tooltip={isFiled ? "Upload Signed T183 Doc" : "Upload T183 Doc"}
                    tooltipOptions={{ position: 'bottom' }}
                    onClick={() => handleUploadClick(rowData, 't183_doc', isFiled ? UPLOAD_COMPANY_SIGNED_TAX_FILING_DOC : UPLOAD_COMPANY_TAX_FILING_DOC)}
                    disabled={isFiled}
                />
                {rowData?.documents?.map((doc: any, index: number) => (
                    doc.file_url && !doc.signed_doc_url && (
                        <Button
                            key={index}
                            icon="pi pi-file-pdf"
                            className="ml-2 p-button-secondary common-view-pdf-btn"
                            tooltip={`View Document ${index + 1}`}
                            tooltipOptions={{ position: 'bottom' }}
                            onClick={() => window.open(doc.file_url, '_blank')}
                        />
                    )
                ))}
                {isFiled && (
                    <Button
                        icon="pi pi-file-pdf"
                        className="p-button-secondary common-view-pdf-btn"
                        tooltip="View Signed T183 Document"
                        tooltipOptions={{ position: 'bottom' }}
                        onClick={() => handleViewPdf(rowData)}
                    />
                )}
            </div>
        );
    };

    return (
        <>
            <Toast ref={toast} />

            <div className="flex justify-content-end mb-3 mr-1">
                <div className="flex align-items-center gap-2">
                    <span className="font-bold" style={{ width: '40%' }}>Select Year: </span>
                    <Calendar
                        value={selectedYear ? new Date(selectedYear, 0, 1) : null}
                        onChange={(e: any) => {
                            const val = e.value as Date;
                            setSelectedYear(val ? val.getFullYear() : null);
                        }}
                        view="year"
                        dateFormat="yy"
                        placeholder="Select Year"
                        className="w-full"
                        showIcon
                    />
                </div>
            </div>

            {!loading ?
                <DataTable
                    ref={dataTableRef}
                    value={list}
                    showGridlines
                    stripedRows
                    responsiveLayout="scroll"
                    scrollable
                    paginator={list?.length > 0}
                    rows={defaultRowOptions}
                    rowsPerPageOptions={defaultPageRowOptions}
                    paginatorTemplate={paginatorLinks}
                    currentPageReportTemplate={showingEntries + " Records"}
                    emptyMessage={<div className="text-center font-bold mx-auto">No filing data found.</div>}
                >
                    {columns.map((col) => {
                        const props = { key: col.field, field: col.field, header: col.header, style: { minWidth: '9rem' } };

                        if (col.field === 'year')
                            return <Column key={col.field} field={col.field} header={col.header} style={{ minWidth: '5rem' }} />;
                        else if (col.field === 'date')
                            return <Column key={col.field} field={col.field} header={col.header} style={{ minWidth: '8rem' }} body={(rowData) => globalDateTemplate(rowData, col)} />;
                        else if (col.field === 'payment')
                            return <Column {...props} body={paymentBodyTemplate} />;
                        else if (col.field === 'bank_rec')
                            return <Column key={col.field} field={col.field} header={col.header} style={{ minWidth: '9rem' }} body={bankRecBodyTemplate} />;
                        else if (col.field === 'document_status')
                            return <Column key={col.field} field={col.field} header={col.header} style={{ minWidth: '9rem' }} body={documentColumnTemplate} />;
                        else if (col.field === 'credit_card')
                            return <Column key={col.field} field={col.field} header={col.header} style={{ minWidth: '9rem' }} body={creditCardBodyTemplate} />;
                        else if (col.field === 'financials')
                            return <Column key={col.field} field={col.field} header={col.header} style={{ minWidth: '9rem' }} body={financialsBodyTemplate} />;
                        // else if (col.field === 'gst_return')
                        //     return <Column key={col.field} field={col.field} header={col.header} style={{ minWidth: '9rem' }} body={gstReturnBodyTemplate} />;
                        // else if (col.field === 'wcb_return')
                        //     return <Column key={col.field} field={col.field} header={col.header} style={{ minWidth: '9rem' }} body={wcbReturnBodyTemplate} />;
                        else if (col.field === 't2_return')
                            return <Column key={col.field} field={col.field} header={col.header} style={{ minWidth: '9rem' }} body={t2ReturnBodyTemplate} />;
                        else if (col.field === 't183_doc')
                            return <Column key={col.field} field={col.field} header={col.header} style={{ minWidth: '12rem' }} body={t183DocBodyTemplate} />;
                        else if (col.field === 't5_return')
                            return <Column key={col.field} field={col.field} header={col.header} style={{ minWidth: '11rem' }} body={t5ReturnBodyTemplate} />;
                        else if (col.field === 't4a_return')
                            return <Column key={col.field} field={col.field} header={col.header} style={{ minWidth: '11rem' }} body={t4aReturnBodyTemplate} />;
                        else if (col.field === 'status')
                            return <Column key={col.field} field={col.field} header={col.header} style={{ minWidth: '10rem' }} body={statusBodyTemplate} />;
                        else if (col.field === 'note')
                            return <Column key={col.field} field={col.field} header={col.header} style={{ minWidth: '4rem' }} body={notesColumnTemplate} />;
                        else
                            return <Column key={col.field} field={col.field} header={col.header} style={{ minWidth: '9rem' }} />;
                    })}
                </DataTable> : (
                    <DataTable value={Skeletonitems}>
                        {columns.map((col: ColumnMeta) => (
                            <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} />
                        ))}
                    </DataTable>
                )
            }

            {/* Modal to confirm status change */}
            <Dialog header="Confirm Status Change" visible={statusModal} style={{ width: '450px' }} className="mx-3 lg:mx-0" draggable={false} modal onHide={() => { setStatusModal(false); setStatusPassword(''); setStatusPasswordError(''); }}
                footer={<div><Button label="Cancel" icon="pi pi-times" onClick={() => { setStatusModal(false); setStatusPassword(''); setStatusPasswordError(''); }} className="p-button-secondary" /><Button label="Confirm" icon="pi pi-check" onClick={saveStatus} className="common-primary-button" loading={submitLoading} autoFocus /></div>}>
                <div className="grid p-fluid formgrid">
                    <div className="field col-12">
                        <label className="font-semibold text-lg">
                            Are you sure you want to change status to <span style={{ color: statusColors[newStatus] || 'inherit', fontWeight: 'bold' }}>{newStatus}</span>{(type === 'gst' || type === 'wcb') ? '' : ` for ${selectedField.replace('_', ' ').toUpperCase()}`}?
                        </label>
                    </div>
                    {selectedFiling?.[selectedField] === 'Done' && (
                        <div className="field col-12">
                            <label className="font-semibold block mb-2">Password <span className="text-red-500">*</span></label>
                            <Password value={statusPassword} onChange={(e) => { setStatusPassword(e.target.value); setStatusPasswordError(''); }} feedback={false} toggleMask placeholder="Enter password" inputClassName={`w-full ${statusPasswordError ? 'p-invalid' : ''}`} className="w-full" />
                            {statusPasswordError && <small className="p-invalid-txt">{statusPasswordError}</small>}
                        </div>
                    )}
                </div>
            </Dialog>

            {/* Modal for document uploads */}
            <Dialog header="Document Upload" visible={uploadModal} style={{ width: '500px' }} className="mx-3 lg:mx-0" draggable={false} modal onHide={() => { setUploadModal(false); setSelectedFile(null); setProfitAndLossFile(null); setBalanceSheetFile(null); setUploadedUrl(''); setUploadErrors({}); }}
                footer={<div><Button label="Cancel" icon="pi pi-times" onClick={() => setUploadModal(false)} className="p-button-secondary" /><Button label="Send" icon="pi pi-send" onClick={saveUpload} className="common-primary-button" loading={submitLoading} autoFocus /></div>}>
                <div className="grid p-fluid formgrid">
                    <div className="field col-12">
                        <div>
                            <label className="font-semibold block mb-2">Upload T183 Document <span className="text-red-500">*</span></label>
                            <FileUploader name="doc_upload" onInputChange={(e: any) => { setSelectedFile(e.files); setUploadErrors({ ...uploadErrors, selectedFile: "" }) }} multiple={false} maxSize={8} value={selectedFile} />
                            {uploadErrors.selectedFile && <small className="p-invalid-txt">{uploadErrors.selectedFile}</small>}
                        </div>

                        {(selectedField === 't183_doc' && activeUploadApi === UPLOAD_COMPANY_TAX_FILING_DOC) && (
                            <>
                                <div className="mt-4">
                                    <label className="font-semibold block mb-2">Upload Profit & Loss Summary <span className="text-red-500">*</span></label>
                                    <FileUploader name="profit_loss_summary" onInputChange={(e: any) => { setProfitAndLossFile(e.files); setUploadErrors({ ...uploadErrors, profitAndLossFile: "" }) }} multiple={false} maxSize={8} value={profitAndLossFile} />
                                    {uploadErrors.profitAndLossFile && <small className="p-invalid-txt">{uploadErrors.profitAndLossFile}</small>}
                                </div>
                                <div className="mt-4">
                                    <label className="font-semibold block mb-2">Upload Balance Sheet <span className="text-red-500">*</span></label>
                                    <FileUploader name="balance_sheet" onInputChange={(e: any) => { setBalanceSheetFile(e.files); setUploadErrors({ ...uploadErrors, balanceSheetFile: "" }) }} multiple={false} maxSize={8} value={balanceSheetFile} />
                                    {uploadErrors.balanceSheetFile && <small className="p-invalid-txt">{uploadErrors.balanceSheetFile}</small>}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </Dialog>

            {/* Modal to show WhatsApp links */}
            <Dialog
                header="WhatsApp Link"
                visible={successModal}
                style={{ width: '500px' }}
                className="mx-3 lg:mx-0"
                draggable={false}
                modal
                onHide={() => setSuccessModal(false)}
                footer={<div><Button label="Close" icon="pi pi-times" onClick={() => setSuccessModal(false)} className="p-button-secondary" /></div>}>
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
                    <CopyAllButton
                        data={successData}
                        onCopySuccess={handleCopyAllFinish}
                        type="company"
                    />
                </div>
                <div className="grid p-fluid formgrid">
                    <div className="field col-12">
                        {(successData?.signUrl || successData?.taxpayerSignUrl) && (
                            <div className="mb-4">
                                <p className="font-bold mb-0">T183 Document Url :</p>
                                <div className="input-effect-div flex gap-2 align-items-center">
                                    {(() => {
                                        const rawUrl = successData.signUrl || successData.taxpayerSignUrl;
                                        let signFullUrl = rawUrl.startsWith('http') ? rawUrl : `${APP_BASE_URL}/${rawUrl.startsWith('/') ? rawUrl.substring(1) : rawUrl}`;
                                        if (!signFullUrl.toLowerCase().includes('type=company')) signFullUrl += (signFullUrl.includes('?') ? '&' : '?') + 'type=company';

                                        return (
                                            <>
                                                <a href={signFullUrl} target="_blank" rel="noreferrer" style={{ color: '#2528d3', wordBreak: 'break-all', fontWeight: '600' }}>
                                                    {signFullUrl}
                                                </a>
                                                <span
                                                    style={{ cursor: 'pointer', display: 'flex' }}
                                                    onClick={() => handleCopy(signFullUrl, "t183")}
                                                    data-pr-tooltip={whatsappT183Copy ? 'Copied' : 'Copy'}
                                                    data-pr-position="top"
                                                >
                                                    {whatsappT183Copy ? <Check size={16} color="#22c55e" /> : <Copy size={16} color="#6b7280" />}
                                                </span>
                                            </>
                                        );
                                    })()}
                                    <Tooltip key={whatsappT183Copy ? 'copied' : 'copy'} />
                                </div>
                            </div>
                        )}

                        {successData?.readDocUrls && successData.readDocUrls.map((rawUrl: string, index: number) => (
                            <div className="mb-4" key={index}>
                                <p className="font-bold mb-0">{index === 0 ? "Profit and Loss Summary" : index === 1 ? "Balance Sheet" : `Read Document ${index + 1}`} Url :</p>
                                <div className="input-effect-div flex gap-2 align-items-center">
                                    {(() => {
                                        if (!rawUrl) return null;
                                        let fileFullUrl = rawUrl.startsWith('http') ? rawUrl : `${APP_BASE_URL}/${rawUrl.startsWith('/') ? rawUrl.substring(1) : rawUrl}`;
                                        if (!fileFullUrl.toLowerCase().includes('type=company')) fileFullUrl += (fileFullUrl.includes('?') ? '&' : '?') + 'type=company';

                                        return (
                                            <>
                                                <a href={fileFullUrl} target="_blank" rel="noreferrer" style={{ color: '#2528d3', wordBreak: 'break-all', fontWeight: '600' }}>
                                                    {fileFullUrl}
                                                </a>
                                                <span
                                                    style={{ cursor: 'pointer', display: 'flex' }}
                                                    onClick={() => handleCopy(fileFullUrl, index === 0 ? "profit_loss" : index === 1 ? "balance_sheet" : null)}
                                                    data-pr-tooltip={(index === 0 ? whatsappProfitAndLossCopy : index === 1 ? whatsappBalanceSheetCopy : false) ? 'Copied' : 'Copy'}
                                                    data-pr-position="top"
                                                >
                                                    {(index === 0 ? whatsappProfitAndLossCopy : index === 1 ? whatsappBalanceSheetCopy : false) ? <Check size={16} color="#22c55e" /> : <Copy size={16} color="#6b7280" />}
                                                </span>
                                            </>
                                        );
                                    })()}
                                    <Tooltip key={(index === 0 ? whatsappProfitAndLossCopy : index === 1 ? whatsappBalanceSheetCopy : false) ? 'copied' : 'copy'} />
                                </div>
                            </div>
                        ))}

                    </div>
                </div>
            </Dialog>

            {/* Modal to manage payments */}
            <Dialog header={paymentValue ? "Resend Payment Link" : "Enter Tax Filing Amount"} visible={paymentModal} style={{ width: '450px' }} className="mx-3 lg:mx-0" draggable={false} modal onHide={() => setPaymentModal(false)}
                footer={<div><Button label="Cancel" icon="pi pi-times" onClick={() => setPaymentModal(false)} className="p-button-secondary" /><Button label="Send" icon="pi pi-send" onClick={savePaymentAmount} className="common-primary-button" loading={submitLoading} /></div>}>
                <div className="grid p-fluid formgrid">
                    <div className="field col-12">
                        <label className="font-bold">Payment Amount</label>
                        <div className="input-effect-div p-inputgroup mt-2">
                            <span className="font-bold p-inputgroup-addon">$</span>
                            <InputNumber id="payment_value" value={paymentValue} onValueChange={(e) => setPaymentValue(e.value)} placeholder="0.00" minFractionDigits={2} maxFractionDigits={2} mode="decimal" className="w-full h-3rem font-bold" />
                        </div>
                    </div>
                </div>
            </Dialog>

            <AddNote
                addNote={addNote}
                hideNotesDialog={() => setAddNote(false)}
                saveNoteHandler={saveNoteHandler}
                noteText={noteText}
                setNoteText={setNoteText}
                noteLoading={noteLoading}
                title={selectedField === 't5_return' ? 'T5 Return Notes' : selectedField === 't4a_return' ? 'T4A Return Notes' : 'Add Note'}
            />
        </>
    );
};

const CompanyFilingTab = () => {
    const { id } = useParams();
    const location = useLocation();
    const state = location.state as { activeIndex?: number, companyName?: string } | null;
    const companyId = id ? atob(id) : '';
    // Active tab index state
    const [activeIndex, setActiveIndex] = useState(state?.activeIndex || 0);
    // Company name display state
    const [companyName, setCompanyName] = useState<string>(state?.companyName || '');

    // Fetch company details on mount
    useEffect(() => {
        if (!companyName && companyId) {
            getCompanyDetails();
        }
    }, [companyId]);

    // Get company name via API
    const getCompanyDetails = async () => {
        try {
            const response = await getAllDetails(LIST_COMPANIES);
            const data = response?.data || response || [];
            const selected = data.find((c: any) => c.id.toString() === companyId);
            if (selected) {
                setCompanyName(selected.company_name);
            }
        } catch (error) { }
    };

    // Sync tab index from state
    useEffect(() => {
        if (state?.activeIndex !== undefined) {
            setActiveIndex(state.activeIndex);
        }
    }, [state?.activeIndex]);

    return (
        <>
            <AppBreadcrumb currentMenu="Company List" pageName="Filing History" pageTitle={`${companyName} Tax Filing History` || "Company Filing History"} goToPage={true} />
            <div className="card mt-4">
                <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                    <TabPanel header="Tax Filing">
                        <FilingTable api={COMPANY_TAX_FILING_LIST} statusApi={COMPANY_TAX_FILING_STATUS_CHANGE} companyId={companyId} columns={TaxFilingColumns} paymentApi={COMPANY_TAX_FILING_PAYMENT} type="tax" />
                    </TabPanel>
                    <TabPanel header="GST Filing">
                        <FilingTable api={COMPANY_GST_FILING_LIST} statusApi={COMPANY_GST_FILING_STATUS_CHANGE} companyId={companyId} columns={GstFilingColumns} type="gst" />
                    </TabPanel>
                    <TabPanel header="WCB Filing">
                        <FilingTable api={COMPANY_WCB_FILING_LIST} statusApi={COMPANY_WCB_FILING_STATUS_CHANGE} companyId={companyId} columns={WcbFilingColumns} type="wcb" />
                    </TabPanel>
                </TabView>
            </div>
        </>
    );
};

export default CompanyFilingTab;
